import { Grid, Menu, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Layout } from '@roc/feature-app-core';
import { ActionsColumn, DataGrid, LinkColumn, Tabs } from '@roc/ui';
import { Roles, SupportRoles } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useCommunicationRoutesConfig } from '@roc/feature-communication';
import { AddLoanTaskDialog, useGeneralTasksStore, TaskPriority } from '@roc/feature-general-tasks';
import { useRocInternalPortalStore } from 'apps/roc-internal-portal/src/app/hooks';
import {
  responseManagementColumns,
  DEFAULT_SORT_MODEL,
  ResponseManagementTab,
  ResponseManagementCategoryTab,
  RESPONSE_MANAGEMENT_CATEGORY_BY_TAB,
  TABS,
  CATEGORY_TABS,
} from '../utils';
import {
  loansWaitingUnderwriteTaskColumns,
  DEFAULT_SORT_MODEL as LOANS_WAITING_UNDERWRITE_TASK_DEFAULT_SORT_MODEL,
} from '../../loansWaitingUnderwriteTask/utils';

const useStyles = makeStyles(() => ({
  grid: {
    height: '70vh',
  },
  categoryTabs: {
    marginBottom: '8px',
  },
}));

export const ResponseManagement = observer(() => {
  const classes = useStyles();
  const { responseManagementStore, loansWaitingUnderwriteTaskStore, userStore } = useRocInternalPortalStore();
  const { gridStore: responseManagementGridStore } = responseManagementStore;
  const { gridStore: loansWaitingUnderwriteTaskGridStore } = loansWaitingUnderwriteTaskStore;
  const history = useHistory();
  const { communicationRoutesConfig } = useCommunicationRoutesConfig();
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore } = generalTasksStore;

  const [selectedTab, setSelectedTab] = useState(ResponseManagementTab.RESPONSE_MANAGEMENT);
  const [selectedCategoryTab, setSelectedCategoryTab] = useState(ResponseManagementCategoryTab.UNDERWRITING);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [currentActionsRow, setCurrentActionsRow] = useState(null);
  const [readyToRender, setReadyToRender] = useState(false);
  const [createCriticalTaskDialogOpen, setCreateCriticalTaskDialogOpen] = useState(false);

  const canMarkConversationAsHandled =
    readyToRender &&
    (userStore.userInformation?.roles?.includes(Roles.BACK_OFFICE_PROCESSOR) ||
      userStore.userInformation?.roles?.includes(SupportRoles.TECH) ||
      userStore.userInformation?.roles?.includes(SupportRoles.TECH_ADMIN) ||
      userStore.userInformation?.roles?.includes(Roles.UNDERWRITER_PRODUCT));

  const canMarkConversationAsHandledRef = useRef(canMarkConversationAsHandled);
  canMarkConversationAsHandledRef.current = canMarkConversationAsHandled;

  useEffect(() => {
    console.log('[ResponseManagement] userInformation:', userStore.userInformation, '- calling getUserInfoAndEntitlements()');
    userStore.getUserInfoAndEntitlements(() => {
      console.log('[ResponseManagement] getUserInfoAndEntitlements loaded, roles:', userStore.userInformation?.roles);
      setReadyToRender(true);
    });
  }, []);

  console.log('[ResponseManagement] readyToRender:', readyToRender, 'canMarkConversationAsHandled:', canMarkConversationAsHandled, 'roles:', userStore.userInformation?.roles);

  const openActionsMenu = (e, row) => {
    setCurrentActionsRow(row);
    setActionsAnchorEl(e.currentTarget);
  };
  const closeActionsMenu = () => {
    setActionsAnchorEl(null);
  };

  const handleMarkConversationAsHandled = () => {
    closeActionsMenu();
    if (currentActionsRow?.twilConversationSid) {
      responseManagementStore.markConversationAsHandled(currentActionsRow.twilConversationSid);
    }
  };

  const handleRespond = () => {
    closeActionsMenu();
    if (currentActionsRow?.loanId && currentActionsRow?.twilConversationSid) {
      history.push(
        communicationRoutesConfig
          .conversations(`${currentActionsRow.loanId}`)
          .children.conversation(currentActionsRow.twilConversationSid).url
      );
    }
  };

  const handleOpenCreateCriticalTask = () => {
    closeActionsMenu();
    setCreateCriticalTaskDialogOpen(true);
  };

  const handleCloseCreateCriticalTask = () => {
    setCreateCriticalTaskDialogOpen(false);
  };

  useEffect(() => {
    if (selectedTab === ResponseManagementTab.LOANS_WAITING_UNDERWRITE_TASK) {
      loansWaitingUnderwriteTaskGridStore.reset();
      loansWaitingUnderwriteTaskGridStore.setSortModel(LOANS_WAITING_UNDERWRITE_TASK_DEFAULT_SORT_MODEL);
    } else {
      responseManagementGridStore.reset();
      responseManagementStore.setSelectedCategory(RESPONSE_MANAGEMENT_CATEGORY_BY_TAB[selectedCategoryTab]);
      responseManagementGridStore.setSortModel(DEFAULT_SORT_MODEL);
    }
  }, [selectedTab, selectedCategoryTab]);

  const frameworkComponents = {
    loanIdCellRenderer: params => (
      <LinkColumn
        value={params.value}
        url={{ pathname: '/loan_information/' + params.value }}
        onClick={e => {
          open('/nportal/#/loan_information/' + params.value);
          e.preventDefault();
        }}
      />
    ),
    responseManagementActionsCellRenderer: params =>
      canMarkConversationAsHandledRef.current
        ? ActionsColumn({
          actions: [
            {
              icon: <MoreVertIcon color="primary" />,
              onClick: e => openActionsMenu(e, params.node.data),
            },
          ],
        })
        : null,
    loansWaitingUnderwriteTaskActionsCellRenderer: params =>
      canMarkConversationAsHandledRef.current
        ? ActionsColumn({
          actions: [
            {
              icon: <MoreVertIcon color="primary" />,
              onClick: e => openActionsMenu(e, params.node.data),
            },
          ],
        })
        : null,
  };

  const onResponseManagementSortChanged = params => {
    responseManagementGridStore.setSortModel(params.api.getSortModel());
  };

  const onResponseManagementFilterChanged = params => {
    responseManagementGridStore.setFilterModel(params.api.getFilterModel());
  };

  const onLoansWaitingUnderwriteTaskSortChanged = params => {
    loansWaitingUnderwriteTaskGridStore.setSortModel(params.api.getSortModel());
  };

  const onLoansWaitingUnderwriteTaskFilterChanged = params => {
    loansWaitingUnderwriteTaskGridStore.setFilterModel(params.api.getFilterModel());
  };

  if (!readyToRender) {
    return <Layout title="Response Management">{null}</Layout>;
  }

  return (
    <Layout title="Response Management">
      <Grid container>
        <Grid item xs={12}>
          <Tabs
            selectedTab={selectedTab}
            tabs={TABS}
            onTabChange={(e, i) => setSelectedTab(i)}
          />
        </Grid>
        {selectedTab === ResponseManagementTab.RESPONSE_MANAGEMENT && (
          <>
            <Grid item xs={12} className={classes.categoryTabs}>
              <Tabs
                selectedTab={selectedCategoryTab}
                tabs={CATEGORY_TABS}
                onTabChange={(e, i) => setSelectedCategoryTab(i)}
              />
            </Grid>
            <Grid item xs={12}>
              <DataGrid
                className={classes.grid}
                columns={responseManagementColumns}
                rows={responseManagementGridStore.gridData.data.rows}
                frameworkComponents={frameworkComponents}
                onSortChanged={onResponseManagementSortChanged}
                onFilterChanged={onResponseManagementFilterChanged}
                sortModel={responseManagementGridStore.sortModel}
                isLoading={responseManagementGridStore.isLoading}
                showFilters
                showPagination
                paginationData={responseManagementGridStore.gridData.meta}
                setPageNumber={responseManagementGridStore.setPageNumber}
              />
            </Grid>
          </>
        )}
        {selectedTab === ResponseManagementTab.LOANS_WAITING_UNDERWRITE_TASK && (
          <Grid item xs={12}>
            <DataGrid
              className={classes.grid}
              columns={loansWaitingUnderwriteTaskColumns}
              rows={loansWaitingUnderwriteTaskGridStore.gridData.data.rows}
              frameworkComponents={frameworkComponents}
              onSortChanged={onLoansWaitingUnderwriteTaskSortChanged}
              onFilterChanged={onLoansWaitingUnderwriteTaskFilterChanged}
              sortModel={loansWaitingUnderwriteTaskGridStore.sortModel}
              isLoading={loansWaitingUnderwriteTaskGridStore.isLoading}
              showFilters
              showPagination
              paginationData={loansWaitingUnderwriteTaskGridStore.gridData.meta}
              setPageNumber={loansWaitingUnderwriteTaskGridStore.setPageNumber}
            />
          </Grid>
        )}
      </Grid>
      <Menu
        id="response-management-actions-menu"
        anchorEl={actionsAnchorEl}
        keepMounted
        open={Boolean(actionsAnchorEl)}
        onClose={closeActionsMenu}
      >
        {selectedTab === ResponseManagementTab.RESPONSE_MANAGEMENT && [
          <MenuItem key="open-conversation" onClick={handleRespond}>Open Conversation</MenuItem>,
          <MenuItem key="mark-as-handled" onClick={handleMarkConversationAsHandled}>Mark as Handled</MenuItem>,
        ]}
        {selectedTab === ResponseManagementTab.LOANS_WAITING_UNDERWRITE_TASK && (
          <MenuItem onClick={handleOpenCreateCriticalTask}>Create Critical Task</MenuItem>
        )}
      </Menu>
      <AddLoanTaskDialog
        open={createCriticalTaskDialogOpen}
        loanId={currentActionsRow?.loanId}
        handleClose={handleCloseCreateCriticalTask}
        options={editTaskStore.loanTaskSectionNames.map(category => ({
          value: category.taskCategoryId,
          label: category.displayName,
        }))}
        defaultPriority={TaskPriority.CRITICAL}
      />
    </Layout>
  );
});
